import React, { useState, useEffect } from "react";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import styles from "./CustomizeStandingPage.module.css";
import PageHeader from "NewsAdmin/Shared/PageHeader";
import FeedBanner from "NewsAdmin/Shared/FeedBanner";
import DeleteFeedItemBar from "NewsAdmin/Shared/DeleteFeedItemBar";
import StandingOrderDropdownList from "NewsAdmin/Shared/StandingOrderDropdownList";
import FeedItemPreview from "NewsAdmin/Shared/FeedItemPreview";
import { useEditFeedPage } from "../EditFeedPage/useEditFeedPage";
import { flattenFeedItemForPut } from "NewsAdmin/utils/flattenFeedItemForPut";
import Loader from "shared/Loader";
import dayjs from "dayjs";
import { useFeedItemOptionsDropdown } from "NewsAdmin/Shared/FeedItemOptionsDropdown/useFeedItemOptionsDropdown";
import NotificationOverlay from "NewsAdmin/Shared/NotificationOverlay";
import ConfirmationDialog from "NewsAdmin/Shared/ConfirmationDialog";
import moment from "moment-timezone";

// deep compare util
const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

export function CustomizeStandingPage({
  isNew,
  isCloning = false,
  justAdded = false,
  feedItem: incomingFeedItem,
  feedItemId,
  setPage,
  diamondsList,
  groupsList,
}) {
  const history = useHistory();
  const hostname = window.location.hostname;

  let defaultImageUrl =
    "https://wwgus.blob.core.windows.net/icons/bsm-image-coming-soon.png"; // prod

  if (hostname.includes("localhost") || hostname.includes("dev")) {
    defaultImageUrl =
      "https://wwgusdev.blob.core.windows.net/home-feed/bsm-image-coming-soon.png";
  }

  const defaultFeedItem = {
    type: "Standing Order",
    imgSrc: defaultImageUrl,
    options: {
      title: "* Standing Order Title *",
      type: "standing-order",
      previewIBO: 0,
      pinLevelPermissions: ["AssociateIbo", "Platinum", "Emerald", "Diamond"],
      familyReunions: [],
      diamonds: [],
      groups: [],
      platforms: ["Web", "iOS", "Android"],
      shouldSendUpdateIcon: true,
      status: 0,
      availableOn: moment()
        .hour(17)
        .minute(0)
        .second(0)
        .millisecond(0)
        .toDate(),
      unpublishOn: "",
      unpublishType: 0,
      isEditingDraft: false,
      managementApproved: false,
      group: 1,
      priority: 5,
    },
    data: {
      card: {
        details: {
          creator: { text: "* Creator's Name Here *", color: "" },
          sku: { text: "* WW#### *", color: "" },
          title: { text: "* Type of Standing Order *", color: "" },
        },
        gradient: {
          angle: 45,
          stops: ["#6998AD", "#fff"],
        },
        header: {
          topTitle: { text: "* Pre-Title Text *", color: "" },
          title: { text: "* New Standing Order Title *", color: "" },
          subTitle: { text: "* New Sub Title *", color: "" },
        },
        image: {
          alignment: "center",
          path: "",
          subText: { text: "* Icon Title *", color: "" },
        },
      },
      content: {
        sections: [],
      },
    },
  };

  // merge defaults + incoming
  const mergedInitial = {
    ...(isNew
      ? defaultFeedItem
      : {
          ...defaultFeedItem,
          ...incomingFeedItem,
          options: {
            ...defaultFeedItem.options,
            ...incomingFeedItem?.options,
          },
          data: {
            ...defaultFeedItem.data,
            ...incomingFeedItem?.data,
            card: {
              ...defaultFeedItem.data.card,
              ...incomingFeedItem?.data?.card,
            },
            content: {
              ...defaultFeedItem.data.content,
              ...incomingFeedItem?.data?.content,
            },
          },
          template: incomingFeedItem?.template || "Standing",
        }),
  };

  const [feedItem, setFeedItem] = useState(deepClone(mergedInitial));
  useEffect(() => {
    //  console.log(feedItem);
  }, [feedItem]);
  const [initialFeedItem] = useState(deepClone(mergedInitial)); // snapshot for reset & change detection
  const [hasChanged, setHasChanged] = useState(isNew);
  const [hasPersistedChangesOnServer, setHasPersistedChangesOnServer] =
    useState(Boolean(justAdded));
  const [isDraft, setIsDraft] = useState(
    incomingFeedItem?.options?.isEditingDraft || false
  );
  const [resetKey, setResetKey] = useState(0);

  const [showAlert, setShowAlert] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const triggerAlert = () => {
    setShowAlert(true);
    setTimeout(() => {
      setShowAlert(false);
    }, 1000);
  };

  const {
    saveFeedItem,
    saveAsDraft,
    createFeedItem,
    deleteFeedItem,
  } = useEditFeedPage();
  const queryClient = useQueryClient();

  // global change tracking
  useEffect(() => {
    setHasChanged(!isEqual(feedItem, initialFeedItem));
  }, [feedItem, initialFeedItem]);

  const onBackArrow = () => {
    if (!hasPersistedChangesOnServer) {
      history.push(`/marketing/news/feed`);
      return;
    }
    history.push(`/marketing/news/feed`, {
      showFeedLoader: true,
      expectedFeedId: feedItem?.options?.id || feedItemId || null,
      expectedFeedTitle: null,
    });
  };

  const findIbos = (array) => {
    const ibos = [];
    array.map((string) => {
      ibos.push(diamondsList.find((diamond) => diamond.name == string).IBO);
    });
    return ibos;
  };

  const onReset = () => {
    if (!isNew) {
      setFeedItem(deepClone(initialFeedItem));
      setResetKey((prevKey) => prevKey + 1);
    } else {
      setPage("create");
    }
  };

  const onSave = () => {
    if (isNew || isCloning) {
      createFeedItem(
        { feedItem, diamondsList, groupsList },
        {
          onSuccess: (itemId) => {
            history.push(`/marketing/news/feed/${itemId}`, {
              pageType: "Standing Order",
              justAdded: true,
            });
          },
          onError: (err) => {
            console.error("Create failed:", err);
          },
        }
      );
    } else {
      saveFeedItem(
        { feedItem, diamondsList, groupsList },
        {
          onSuccess: (updatedItem) => {
            const merged = deepClone({
              ...feedItem,
              ...updatedItem,
              data: {
                ...feedItem.data,
                ...updatedItem.data,
                card: {
                  ...feedItem.data.card,
                  ...updatedItem.data?.card,
                },
                content: {
                  ...feedItem.data.content,
                  ...updatedItem.data?.content,
                },
              },
            });
            setFeedItem(merged);
            queryClient.setQueryData(["feedItem", updatedItem.Id], merged);
            queryClient.invalidateQueries(["feed"]);
            setIsDraft(false);
            setHasChanged(false);
            setHasPersistedChangesOnServer(true);
            setResetKey((prevKey) => prevKey + 1);
            triggerAlert();
          },
          onError: (err) => console.error("Save failed: " + err.message),
        }
      );
    }
  };

  const onDraft = () => {
    saveAsDraft(
      { feedItem, diamondsList, groupsList },
      {
        onSuccess: async (updatedItem) => {
          const merged = deepClone({
            ...feedItem,
            ...updatedItem,
            data: {
              ...feedItem.data,
              ...updatedItem.data,
              card: {
                ...feedItem.data.card,
                ...updatedItem.data?.card,
              },
              content: {
                ...feedItem.data.content,
                ...updatedItem.data?.content,
              },
            },
          });
          setFeedItem(merged);
          queryClient.setQueryData(["feedItem", updatedItem.Id], merged);
          queryClient.invalidateQueries(["feed"]);
          setIsDraft(true);
          setHasChanged(false);
          setHasPersistedChangesOnServer(true);
          setResetKey((prevKey) => prevKey + 1);
          triggerAlert();
        },
        onError: (err) => console.error("Draft failed: " + err.message),
      }
    );
  };

  const onDelete = () => {
    setShowDeleteConfirm(true);
  };

  const onConfirmDelete = () => {
    setShowDeleteConfirm(false);
    deleteFeedItem(
      { id: feedItem.options.id || feedItemId },
      {
        onSuccess: () => {
          history.push("/marketing/news/feed");
        },
        onError: (err) => {
          console.error("Delete failed:", err);
        },
      }
    );
  };

  if (!feedItem) {
    return <Loader />;
  }

  return (
    <div className={styles.pageContainer}>
      <ConfirmationDialog
        isVisible={showDeleteConfirm}
        confirmationText="Are you sure you want to delete this feed item? This action cannot be undone."
        onConfirmation={onConfirmDelete}
        onDismiss={() => setShowDeleteConfirm(false)}
      />
      <PageHeader onClick={onBackArrow} isHome={false} />
      <div className={styles.scrollableList}>
        <FeedBanner
          feedType={feedItem.type}
          feedTitle={feedItem.options?.title || ""}
          status={isNew ? "New" : isCloning ? "Clone" : "Edit"}
          isChangedOrNew={isNew ? true : hasChanged}
          isDraft={isDraft}
          showDraft={!isNew && !isCloning}
          onCancel={onBackArrow}
          onReset={onReset}
          onSave={onSave}
          onDraft={onDraft}
        />
        <div className={styles.contentContainer}>
          <div className={styles.flex}>
            <div className={styles.dropdown} key={resetKey}>
              <StandingOrderDropdownList
                feedItem={feedItem}
                setFeedItem={setFeedItem}
                isNew={isNew}
              />
            </div>
            <div className={styles.preview}>
              <FeedItemPreview feedItem={feedItem} />
            </div>
          </div>
        </div>
        {!isDraft && !isNew ? (
          <DeleteFeedItemBar onClick={onDelete} />
        ) : (
          <div className={styles.emptyFooterBar} />
        )}
      </div>

      <NotificationOverlay
        isVisible={showAlert}
        overlayText="Feed Item Updated"
      />
    </div>
  );
}
